import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, FieldPath, getDoc, onSnapshot, runTransaction } from 'firebase/firestore';
import { auth, db } from '../firebase-config';
import TrafficCourseStageViewer from '../components/TrafficCourseStageViewer';
import { getTrafficStageStatusLabel } from '../trafficCourseFlowUtils';

const ENDPOINT = 'https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/advanceTrafficDayProgram';
const validId = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,200}$/.test(value);
const card = { marginTop: 20, padding: 20, border: '1px solid #dbe1e8', borderRadius: 12, background: '#fff' };
const button = { display: 'inline-block', minHeight: 44, padding: '10px 14px', border: '1px solid #bdc6d0', borderRadius: 8, background: '#fff', color: '#243142', font: 'inherit', cursor: 'pointer' };
const row = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '14px 0', borderTop: '1px solid #e5e7eb' };
const phases = { day1: 'Day 1', day2: 'Day 2', practical: 'Practical assessment' };

function Program({ enrolmentId, session }) {
  const [progress, setProgress] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [flows, setFlows] = useState({});
  const [flowErrors, setFlowErrors] = useState({});
  const [selected, setSelected] = useState(null);
  const [releasing, setReleasing] = useState('');
  const [releaseError, setReleaseError] = useState('');
  const [releaseNotice, setReleaseNotice] = useState('');
  const releaseInFlight = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setBusy(true);
    setError('');
    async function advance() {
      try {
        const token = await session.user.getIdToken();
        if (!active) return;
        const response = await fetch(ENDPOINT, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ enrolmentId }), signal: controller.signal, cache: 'no-store',
        });
        if (!active) return;
        if (!response.ok) {
          if ([401, 403].includes(response.status)) {
            setDenied(true); setProgress(null); setSelected(null);
            throw new Error('Staff access denied. Sign in as the assigned trainer or an administrator, then retry.');
          }
          throw new Error('Unable to refresh progress. Check the booking and your connection, then retry.');
        }
        let next;
        try { next = await response.json(); }
        catch (_) { throw new Error('Invalid progress response. Please retry.'); }
        if (!active) return;
        if (!next || !phases[next.nextPhase] || typeof next.ready !== 'boolean' || typeof next.practicalEligible !== 'boolean'
          || ![next.day1FlowId, next.day2FlowId].every(id => id === null || validId(id))
          || (next.practicalBookingId != null && !validId(next.practicalBookingId))
          || typeof next.practicalDate !== 'string') throw new Error('Invalid progress response. Please retry.');
        setProgress(next);
        setDenied(false);
        setSelected(current => current && [next.day1FlowId, next.day2FlowId].includes(current.flowDoc.id) ? current : null);
      } catch (failure) {
        if (active) setError(failure instanceof TypeError ? 'Unable to refresh progress. Check your connection, then retry.' : failure.message);
      } finally { if (active) setBusy(false); }
    }
    advance();
    return () => { active = false; controller.abort(); };
  }, [enrolmentId, session, refresh]);

  const day1 = denied ? null : progress?.day1FlowId;
  const day2 = denied ? null : progress?.day2FlowId;
  useEffect(() => {
    let active = true;
    setFlows({}); setFlowErrors({});
    const unsubscribes = [];
    [...new Set([day1, day2].filter(Boolean))].forEach(id => {
      const unavailable = message => {
        if (!active) return;
        setFlowErrors(current => ({ ...current, [id]: message }));
        setFlows(current => ({ ...current, [id]: null }));
        setSelected(current => current?.flowDoc.id === id ? null : current);
      };
      try {
        unsubscribes.push(onSnapshot(doc(db, 'trafficCourseFlows', id), snapshot => {
          if (!active) return;
          if (!snapshot.exists()) { unavailable('This day session is missing. Refresh progress or contact an administrator.'); return; }
          const flow = { ...snapshot.data(), id: snapshot.id };
          if (flow.status === 'cancelled' || flow.classStatus === 'cancelled') { unavailable('This day session is cancelled.'); return; }
          setFlows(current => ({ ...current, [id]: flow }));
          setFlowErrors(current => ({ ...current, [id]: '' }));
          // The viewer owns its own live subscription and drafts. Never replace
          // its initial flow prop just because a row or PDF metadata changed.
        }, () => unavailable('Unable to read this day session. Check staff access and refresh progress.')));
      } catch (_) { unavailable('Unable to subscribe to this day session. Please reopen the page.'); }
    });
    return () => { active = false; unsubscribes.forEach(unsubscribe => unsubscribe()); };
  }, [day1, day2, refresh]);

  async function release(flow, task) {
    if (releaseInFlight.current || denied || flow.taskStates?.[task.id]?.status !== 'locked' || task.type === 'Trainer/Assessor Declaration') return;
    releaseInFlight.current = true;
    setReleasing(`${flow.id}:${task.id}`); setReleaseError(''); setReleaseNotice('');
    try {
      await runTransaction(db, async transaction => {
        const ref = doc(db, 'trafficCourseFlows', flow.id);
        const snapshot = await transaction.get(ref);
        const current = snapshot.exists() ? snapshot.data() : null;
        if (auth.currentUser?.uid !== session.user.uid || !current || current.status === 'cancelled' || current.classStatus === 'cancelled'
          || current.taskStates?.[task.id]?.status !== 'locked' || !current.tasks?.some(item => item.id === task.id && item.type !== 'Trainer/Assessor Declaration')) {
          throw new Error('The section changed. Review its latest status before releasing.');
        }
        transaction.update(ref, new FieldPath('taskStates', task.id, 'status'), 'available', 'updatedAt', new Date().toISOString());
      });
      if (alive.current) setReleaseNotice('Section release confirmed. Live status will update shortly.');
    } catch (_) {
      if (alive.current) setReleaseError('Release was not confirmed. Check access and the latest section status before retrying.');
    } finally {
      releaseInFlight.current = false;
      if (alive.current) setReleasing('');
    }
  }

  function select(flowDoc, taskId) {
    if (selected?.flowDoc.id === flowDoc.id && selected.taskId === taskId) return;
    if (selected && !window.confirm('Leave the current section? Save any unsaved work before switching.')) return;
    setSelected({ flowDoc, taskId });
  }
  function close() {
    if (window.confirm('Close this section? Any unsaved work will be lost.')) setSelected(null);
  }

  return <>
    <button type='button' style={button} disabled={busy || Boolean(releasing)} onClick={() => setRefresh(value => value + 1)}>Refresh progress</button>
    <p role='status'>{busy ? 'Checking program progress...' : 'Refresh after completing a day or when the next training date arrives. This may provision Day 2.'}</p>
    {error && <p role='alert'>{error}</p>}
    {progress && !denied && <>
      <section style={card} aria-label='Program progress'>
        <h2>Next phase: {phases[progress.nextPhase]}</h2>
        <p>{progress.ready ? 'Next phase is open.' : 'Next phase is not open yet. Refresh on the scheduled date.'}</p>
        <p>Practical date: {progress.practicalDate}</p>
        <p>Practical availability follows its separate booking date, independently of Day 1 and Day 2 completion.</p>
        {progress.practicalEligible && progress.practicalBookingId && !busy && !error
          ? <Link style={button} to={`/trafficPracticalAssessment?bookingId=${encodeURIComponent(progress.practicalBookingId)}`}>Open practical assessment</Link>
          : <button type='button' style={button} disabled>Practical assessment not available</button>}
      </section>
      {[{ number: 1, id: day1 }, { number: 2, id: day2 }].map(day => {
        const flow = flows[day.id];
        return <section key={day.number} style={card} aria-label={`Day ${day.number}`}>
          <h2>Day {day.number}</h2>
          {!day.id ? <p>{day.number === 1 ? 'Not provisioned yet.' : 'Not provisioned or not required for this enrolment.'}</p>
            : flowErrors[day.id] ? <p role='alert'>{flowErrors[day.id]}</p>
              : !flow ? <p>Loading day session...</p> : <>
                <p>{flow.firstName} {flow.lastName} / {flow.courseName || `Day ${day.number}`} / {flow.courseDate}</p>
                <a style={button} href={`/trafficCourseFlow?sid=${encodeURIComponent(day.id)}`} target='_blank' rel='noopener noreferrer'>Open Day {day.number} student view</a>
                <p>This link opens the student assessment, not staff marking.</p>
                {(flow.exclusions || []).map(exclusion => <p key={exclusion.taskId} style={{ padding: 12, borderRadius: 8, background: '#fff7ed', color: '#85400f' }}>
                  Not required for this enrolment: {exclusion.name}
                </p>)}
                {(flow.tasks || []).map(task => <div key={task.id} style={row}>
                  <div style={{ flex: '1 1 240px' }}><strong>{task.name || task.type}</strong><br />{getTrafficStageStatusLabel(flow.taskStates?.[task.id])}</div>
                  <button type='button' style={button} onClick={() => select(flow, task.id)}>Review {task.name || task.type}</button>
                  {flow.taskStates?.[task.id]?.status === 'locked' && task.type !== 'Trainer/Assessor Declaration'
                    && <button type='button' style={button} disabled={Boolean(releasing) || busy} onClick={() => release(flow, task)}>Release {task.name || task.type}</button>}
                </div>)}
              </>}
        </section>;
      })}
      {releaseError && <p role='alert'>{releaseError}</p>}
      {releaseNotice && <p role='status'>{releaseNotice}</p>}
      {selected && <section style={card} aria-label='Staff section review'>
        <h2>Staff section review</h2>
        <p>Save your work before changing sections or leaving this page.</p>
        <button type='button' style={button} onClick={close}>Close section</button>
        <TrafficCourseStageViewer key={`${selected.flowDoc.id}:${selected.taskId}`} flowDoc={selected.flowDoc}
          taskId={selected.taskId} userData={session.profile} />
      </section>}
    </>}
  </>;
}

function StaffProgram({ enrolmentId }) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('Checking staff sign-in...');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    let generation = 0;
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (!active) return;
      const requestGeneration = ++generation;
      setSession(null); setError('');
      if (!user) { setStatus(''); setError('Please sign in with your staff account, then reopen this page.'); return; }
      setStatus('Loading staff profile...');
      try {
        const snapshot = await getDoc(doc(db, 'users', user.uid));
        if (!active || generation !== requestGeneration) return;
        const profile = snapshot.exists() ? snapshot.data() : null;
        if (!['admin', 'trainer'].includes(profile?.role)) { setError('Staff access required. Use a trainer or administrator account.'); return; }
        setSession({ user, profile });
      } catch (_) {
        if (active && generation === requestGeneration) setError('Unable to load your staff profile. Check access and reopen this page.');
      } finally { if (active && generation === requestGeneration) setStatus(''); }
    }, () => {
      if (!active) return;
      ++generation; setSession(null); setStatus(''); setError('Unable to check authentication. Please reopen this page.');
    });
    return () => { active = false; ++generation; unsubscribe(); };
  }, []);
  return <>
    {status && <p role='status'>{status}</p>}
    {error && <p role='alert'>{error}</p>}
    {session && <Program key={session.user.uid} enrolmentId={enrolmentId} session={session} />}
  </>;
}

export default function TrafficDayProgram() {
  const { search } = useLocation();
  const enrolmentId = new URLSearchParams(search).get('enrolmentId');
  return <main style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px 60px', background: 'linear-gradient(140deg, #fff7ed, #f8fafc 55%)', color: '#243142', fontFamily: "'Rubik', sans-serif", lineHeight: 1.6 }}>
    <header style={{ borderTop: '5px solid #c65313', padding: '20px 0' }}>
      <p>UPSKILL INSTITUTE / STAFF PROGRAM VIEW</p><h1>Traffic day program</h1>
      <p>For trusted future booking links. This page does not enrol learners or change existing sessions.</p>
    </header>
    {validId(enrolmentId) ? <StaffProgram key={enrolmentId} enrolmentId={enrolmentId} />
      : <p role='alert'>Open a valid booking link containing an enrolmentId. Enrolments cannot be created here.</p>}
  </main>;
}
